// Inline error banner. Plan 24.
//
// Replaces the 20+ copies of
//   `<div class="p-4 bg-red-50 border border-red-100 text-red-600 ...">...</div>`
// across forms. By default the text passes through `messageFor(code)`
// so we never render the raw server prose — plan 22 locked this
// allowlist down. Call sites that already resolved the string (e.g. a
// local fallback) can pass `message` directly; callers that only have a
// code should pass `code` and we do the lookup.

import type { HTMLAttributes, ReactNode } from 'react';
import { AlertCircle } from '../icons';
import { cn } from '../utils/cn';
import { messageFor } from '../errors/messageFor';

export interface ErrorAlertProps extends HTMLAttributes<HTMLDivElement> {
  /** Pre-resolved user-facing message. Overrides `code`. */
  message?: ReactNode;
  /** Known error code; resolved via `messageFor`. */
  code?: string | null;
  /** Fallback copy when neither `message` nor `code` resolves. */
  fallback?: string;
  /** Optional requestId for support — surfaced in small print. */
  requestId?: string;
}

export const ErrorAlert = ({
  message,
  code,
  fallback,
  requestId,
  className,
  ...rest
}: ErrorAlertProps) => {
  const resolved = message ?? messageFor(code, fallback ?? 'Something went wrong.');
  if (resolved === null || resolved === undefined || resolved === '') return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl flex items-start gap-3',
        className
      )}
      {...rest}
    >
      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div className="space-y-1">
        <p className="text-sm font-medium">{resolved}</p>
        {requestId && <p className="text-[10px] font-mono text-red-500/70">Ref: {requestId}</p>}
      </div>
    </div>
  );
};
